import React, { useState, useEffect, useCallback, useMemo, use } from "react";
import { Button, Modal } from "semantic-ui-react";
import SearchPatient from "../../../../components/SearchPatient/index";
import { Formik } from "formik";
import { orderedSpecialRegimes } from "utils/selectOptions";
import moment from "moment";
import { cleanPhoneNumber } from "utils/phone";
import * as Yup from "yup";
import { isValidPhoneNumber, formatPhoneNumber } from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import * as cdigit from "cdigit";
import GranteeForm from "./GranteeForm";
import { convertMyLabToPatient } from "utils/patientConverter";

const GranteePatientModal = ({
	grantee,
	submit,
	open,
	close,
	contactComments,
	searchByMatricule,
	t,
	specialRegimes,
	foundGrantee,
	initFoundGrantee,
}) => {
	const [granteeSelected, setGranteeSelected] = useState(grantee?.grantee || null);
	const [editPatientPanel, setEditPatientPanel] = useState(!!grantee?.grantee);
	const [patient, setPatient] = useState(grantee?.grantee || { id: 0 });
	const [relation, setRelation] = useState(grantee?.relation || "");
	const [tutor, setTutor] = useState(null);

	useEffect(() => {
		setGranteeSelected(grantee?.grantee || null);
		setEditPatientPanel(!!grantee?.grantee);
		setPatient(grantee?.grantee || { id: 0 });
		setRelation(grantee?.relation || "");
		setTutor(grantee?.grantee || null);
	}, [grantee]);

	const selectCallback = useCallback((value) => {
		console.log("selectCallback value", value);
		const patient = convertMyLabToPatient(value);
		setGranteeSelected(patient);
		setEditPatientPanel(true);
		setPatient(patient);
	}, []);

	const addNewPatientCallback = useCallback(() => {
		setGranteeSelected({ id: 0 });
		setEditPatientPanel(true);
	}, []);

	const handleConfirm = async () => {
		const grantee = { ...tutor, gender: tutor?.sex === "male" ? "m" : tutor?.sex === "female" ? "f" : "" };
		console.log("submit", grantee, tutor.relation);
		submit(grantee, tutor.relation);
		closeModal();
	};

	const closeModal = () => {
		setEditPatientPanel(false);
		initFoundGrantee();
		setRelation("");
		setGranteeSelected(null);
		setPatient({ id: 0 });
		close();
	};

	const specialRegimeOptions = useMemo(() => orderedSpecialRegimes(specialRegimes?.list), [specialRegimes?.list]);
	const findSpecialRegime = useCallback(
		(healthFundId) => specialRegimes?.list.find((regime) => regime.geslab_id === healthFundId)?.id || null,
		[specialRegimes]
	);

	const schemaValidation = Yup.object().shape({
		birth_name: Yup.string().required("Champ requis"),
		first_name: Yup.string().required("Champ requis"),
		birth_date: Yup.date().required("Champ requis"),
		sex: Yup.string().required("Champ requis"),
		relation: Yup.string().required("Champ requis"),
		cns: Yup.string().when("ignoreCnsError", {
			is: false,
			then: Yup.string()
				.required("CNS requis")
				.test(
					"compareWithBirthDate",
					"Le numéro CNS et la date de naissance ne sont pas similaires",
					//Yup ref doesn't work with functional arrows
					function (value) {
						if (!value || value === undefined) return false;
						if (value?.length === 8 || value?.length === 13) {
							const ref = Yup.ref("birth_date");
							const birthDate = moment(this.resolve(ref)).format("YYYYMMDD");
							const birthDate2000 = moment(this.resolve(ref)).add(2000, "year").format("YYYYMMDD");
							const cnsBirthDate = value.substring(0, 8);
							return birthDate === cnsBirthDate || birthDate2000 === cnsBirthDate;
						}
						return true;
					}
				)
				.test("cnsLength", "Le numéro CNS doit être composé de 8 ou 13 chiffres", (value) => {
					if (!value || value === undefined) return false;
					return value.match(new RegExp("^(([0-9]{13})|([0-9]{8}))"));
				})
				.test("cnsValidation", "Numéro CNS invalide", (value) => {
					if (!value || value === undefined) return false;

					if (value?.length === 8) return value.match(new RegExp(process.env.REACT_APP_NO_CNS_REGEX));

					if (value?.length === 13) {
						const stringToValidate = value.substring(0, 11);
						const luhnVal = value.charAt(11);
						const verhoeffVal = value.charAt(12);
						return (
							value.match(new RegExp(process.env.REACT_APP_CNS_REGEX)) &&
							cdigit.luhn.compute(stringToValidate) === luhnVal &&
							cdigit.verhoeff.compute(stringToValidate) === verhoeffVal
						);
					}

					return false;
				}),
		}),
		email: Yup.string().email("Email incorrect"),
		mobile_phone: Yup.string()
			.min(2, "Longueur min : 2 caractères")
			.max(50, "Longueur max : 50 caractères")
			.test("phoneTypeValidation", "Il ne s'agit pas d'un numéro de téléphone mobile", (value) => {
				if (!value || value === "+352") return true;
				if (formatPhoneNumber(value) !== "") {
					const type = parsePhoneNumberFromString(value).getType();
					return type === "MOBILE" || type === "FIXED_LINE_OR_MOBILE";
				}
			})
			.test("phoneValidation", "Le format du téléphone mobile est inconnu", (value) => {
				if (!value || value === "+352") return true;
				return isValidPhoneNumber(cleanPhoneNumber(value));
			}),
	});

	const formIsDisabled = patient.id !== 0 || !!patient.mylabId || patient.externalIds?.length > 0 || false;

	return (
		<Modal size="small" open={open} closeOnEscape closeOnDimmerClick onClose={closeModal}>
			<Modal.Header>Donner l'accès à un tuteur</Modal.Header>

			{!editPatientPanel && (
				<Modal.Content>
					<SearchPatient
						placeholder="Sélectionner une personne"
						isSelectSearch
						selectCallback={selectCallback}
						addNewPatientCallback={addNewPatientCallback}
						tutorOnly={true}
					/>
				</Modal.Content>
			)}

			{!!editPatientPanel && (
				<Modal.Content>
					<Formik
						enableReinitialize
						initialValues={{
							tutor: {
								id: patient?.id || 0,
								specialRegime: {
									id: patient?.specialRegime?.id || findSpecialRegime(patient?.health_fund_id) || null,
								},
								mylabId: patient?.externalIds?.length ? patient?.externalIds[0].external_id : patient.mylabId || 0,
								birth_name: patient?.birth_name || patient?.last_name || "",
								first_name: patient?.first_name || "",
								married_name: patient?.married_name || "",
								birth_date: patient?.birth_date || "",
								cns: patient?.cns || "",
								mobile_phone: cleanPhoneNumber(patient?.mobile_phone || patient?.phone) || "",
								email: patient?.email || "",

								sex:
									(patient?.gender === "m" || patient?.gender === "male"
										? "male"
										: patient?.gender === "f" || patient?.gender === "female"
										? "female"
										: "") || "",
								relation: relation || "",
							},
						}}
						validationSchema={schemaValidation}
					>
						{({ values, setFieldValue, errors, touched }) => {
							console.log("Formik tutor : ", values.tutor, " and patient : ", patient);
							return (
								<GranteeForm
									values={values}
									setFieldValue={setFieldValue}
									errors={errors}
									searchByMatricule={searchByMatricule}
									contactComments={contactComments}
									specialRegimeOptions={specialRegimeOptions}
									foundGrantee={foundGrantee}
									formIsDisabled={formIsDisabled}
									initFoundGrantee={initFoundGrantee}
									setRelation={setRelation}
									setTutor={setTutor}
									touched={touched}
								/>
							);
						}}
					</Formik>
				</Modal.Content>
			)}

			<Modal.Actions>
				<Button
					onClick={(e) => {
						if (e !== undefined) e.preventDefault();
						closeModal();
					}}
				>
					Annuler
				</Button>
				{!!editPatientPanel && (
					<Button
						primary
						disabled={!tutor || !tutor?.specialRegime || !tutor?.relation}
						onClick={(e) => {
							if (e !== undefined) e.preventDefault();
							handleConfirm();
						}}
					>
						Confirmer
					</Button>
				)}
			</Modal.Actions>
		</Modal>
	);
};

export default GranteePatientModal;
